import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as inventoryService from "../services/inventory.service";

export const fetchAllInventories = createAsyncThunk(
  "inventories/fetchAll",
  async (_, thunkAPI) => {
    try {
      return await inventoryService.getAllInventories();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch all inventories");
    }
  }
);

export const fetchActiveInventories = createAsyncThunk(
  "inventories/fetchActive",
  async (_, thunkAPI) => {
    try {
      return await inventoryService.getActiveInventories();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch active inventories");
    }
  }
);

export const fetchInactiveInventories = createAsyncThunk(
  "inventories/fetchInactive",
  async (_, thunkAPI) => {
    try {
      return await inventoryService.getInactiveInventories();
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch inactive inventories");
    }
  }
);

export const fetchInventoryById = createAsyncThunk(
  "inventories/fetchById",
  async (id, thunkAPI) => {
    try {
      return await inventoryService.getInventoryById(id);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to fetch inventory");
    }
  }
);

export const createInventoryWithStockLines = createAsyncThunk(
  "inventories/createWithStockLines",
  async (request, thunkAPI) => {
    try {
      return await inventoryService.createInventoryWithStockLines(request);
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to create inventory with stock lines");
    }
  }
);

export const updateInventory = createAsyncThunk(
  "inventories/update",
  async ({ id, inventory }, thunkAPI) => {
    try {
      await inventoryService.updateInventory(id, inventory);
      return { id, ...inventory };
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to update inventory");
    }
  }
);

// ICI: RabbitMQ / SignalR
export const openInventory = createAsyncThunk(
  "inventories/open",
  async (id, thunkAPI) => {
    try {
      return await inventoryService.openInventory(id); // { jobId, inventoryId, status, message }
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to open inventory");
    }
  }
);

export const closeInventory = createAsyncThunk(
  "inventories/close",
  async (id, thunkAPI) => {
    try {
      return await inventoryService.closeInventory(id); // { jobId, inventoryId, status, message }
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to close inventory");
    }
  }
);

export const softDeleteInventory = createAsyncThunk(
  "inventories/softDelete",
  async (id, thunkAPI) => {
    try {
      await inventoryService.softDeleteInventory(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to soft delete inventory");
    }
  }
);

export const hardDeleteInventory = createAsyncThunk(
  "inventories/hardDelete",
  async (id, thunkAPI) => {
    try {
      await inventoryService.hardDeleteInventory(id);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue("Failed to hard delete inventory");
    }
  }
);

const inventorySlice = createSlice({
  name: "inventories",
  initialState: {
    all: [],
    active: [],
    inactive: [],
    selected: null,
    loading: false,
    error: null,
    showModal: false,
    showDeleteModal: false,

    openInventoryJob: null,
    closeInventoryJob: null,
    openInventoryJobStatus: "idle",
    closeInventoryJobStatus: "idle",
  },
  reducers: {
    openInventoryModal: (state) => {
      state.showModal = true;
    },
    closeInventoryModal: (state) => {
      state.showModal = false;
    },
    openInventoryDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeInventoryDeleteModal: (state) => {
      state.showDeleteModal = false;
    },

    openInventorySocketSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
      state.openInventoryJobStatus = "completed";

      if (
        state.openInventoryJob?.jobId &&
        state.openInventoryJob.jobId !== action.payload?.jobId
      ) {
        return;
      }

      const inventoryId = action.payload?.inventoryId;
      const idxAll = state.all.findIndex((p) => p.inventoryId === inventoryId);
      if (idxAll !== -1) {
        state.all[idxAll] = {
          ...state.all[idxAll],
          inventoryIsActive: true,
          inventoryIsClosed: false,
        };
      }

      const idxInactive = state.inactive.findIndex((p) => p.inventoryId === inventoryId);
      if (idxInactive !== -1) {
        const item = { ...state.inactive[idxInactive], inventoryIsActive: true, inventoryIsClosed: false };
        state.inactive.splice(idxInactive, 1);
        state.active.unshift(item);
      }

      const idxSelected = state.selected?.inventoryId === inventoryId;
      if (idxSelected) {
        state.selected = {
          ...state.selected,
          inventoryIsActive: true,
          inventoryIsClosed: false,
        };
      }
    },

    openInventorySocketFailed: (state, action) => {
      state.loading = false;
      state.openInventoryJobStatus = "failed";

      if (
        state.openInventoryJob?.jobId &&
        action.payload?.jobId &&
        state.openInventoryJob.jobId !== action.payload.jobId
      ) {
        return;
      }

      state.error = action.payload?.error || "Une erreur est survenue.";
    },

    closeInventorySocketSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
      state.closeInventoryJobStatus = "completed";

      if (
        state.closeInventoryJob?.jobId &&
        state.closeInventoryJob.jobId !== action.payload?.jobId
      ) {
        return;
      }

      const inventoryId = action.payload?.inventoryId;
      const idxAll = state.all.findIndex((p) => p.inventoryId === inventoryId);
      if (idxAll !== -1) {
        state.all[idxAll] = {
          ...state.all[idxAll],
          inventoryIsActive: false,
          inventoryIsClosed: true,
        };
      }

      const idxActive = state.active.findIndex((p) => p.inventoryId === inventoryId);
      if (idxActive !== -1) {
        const item = { ...state.active[idxActive], inventoryIsActive: false, inventoryIsClosed: true };
        state.active.splice(idxActive, 1);
        state.inactive.unshift(item);
      }

      const idxSelected = state.selected?.inventoryId === inventoryId;
      if (idxSelected) {
        state.selected = {
          ...state.selected,
          inventoryIsActive: false,
          inventoryIsClosed: true,
        };
      }
    },

    closeInventorySocketFailed: (state, action) => {
      state.loading = false;
      state.closeInventoryJobStatus = "failed";

      if (
        state.closeInventoryJob?.jobId &&
        action.payload?.jobId &&
        state.closeInventoryJob.jobId !== action.payload.jobId
      ) {
        return;
      }

      state.error = action.payload?.error || "Une erreur est survenue.";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllInventories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllInventories.fulfilled, (state, action) => {
        state.loading = false;
        state.all = action.payload;
      })
      .addCase(fetchAllInventories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchActiveInventories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveInventories.fulfilled, (state, action) => {
        state.loading = false;
        state.active = action.payload;
      })
      .addCase(fetchActiveInventories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchInactiveInventories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInactiveInventories.fulfilled, (state, action) => {
        state.loading = false;
        state.inactive = action.payload;
      })
      .addCase(fetchInactiveInventories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchInventoryById.fulfilled, (state, action) => {
        state.selected = action.payload;
      })

      .addCase(createInventoryWithStockLines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInventoryWithStockLines.fulfilled, (state, action) => {
        state.loading = false;
        state.all.unshift(action.payload);
        state.showModal = false;
      })
      .addCase(createInventoryWithStockLines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(updateInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInventory.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.all.findIndex((p) => p.inventoryId === action.payload.id);
        if (idx !== -1) {
          state.all[idx] = { ...state.all[idx], ...action.payload };
        }
        state.showModal = false;
      })
      .addCase(updateInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ICI: accepted only
      .addCase(openInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.openInventoryJobStatus = "pending";
        state.openInventoryJob = null;
      })
      .addCase(openInventory.fulfilled, (state, action) => {
        state.loading = true;
        state.error = null;
        state.openInventoryJobStatus = "pending";
        state.openInventoryJob = action.payload;
      })
      .addCase(openInventory.rejected, (state, action) => {
        state.loading = false;
        state.openInventoryJobStatus = "failed";
        state.error = action.payload;
      })

      .addCase(closeInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.closeInventoryJobStatus = "pending";
        state.closeInventoryJob = null;
      })
      .addCase(closeInventory.fulfilled, (state, action) => {
        state.loading = true;
        state.error = null;
        state.closeInventoryJobStatus = "pending";
        state.closeInventoryJob = action.payload;
      })
      .addCase(closeInventory.rejected, (state, action) => {
        state.loading = false;
        state.closeInventoryJobStatus = "failed";
        state.error = action.payload;
      })

      .addCase(softDeleteInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(softDeleteInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.all = state.all.filter((p) => p.inventoryId !== action.payload);
        state.showDeleteModal = false;
      })
      .addCase(softDeleteInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(hardDeleteInventory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(hardDeleteInventory.fulfilled, (state, action) => {
        state.loading = false;
        state.all = state.all.filter((p) => p.inventoryId !== action.payload);
        state.showDeleteModal = false;
      })
      .addCase(hardDeleteInventory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  openInventoryModal,
  closeInventoryModal,
  openInventoryDeleteModal,
  closeInventoryDeleteModal,
  openInventorySocketSuccess,
  openInventorySocketFailed,
  closeInventorySocketSuccess,
  closeInventorySocketFailed,
} = inventorySlice.actions;

export default inventorySlice.reducer;